import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, createVNode as _createVNode, withCtx as _withCtx, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "relative ml-4 flex items-center gap-2 rounded-md bg-primary-wash pl-4 pr-1 dark:bg-grey-800" }
const _hoisted_2 = { class: "mb-0 inline-flex flex-wrap text-sm gap-x-2" }
const _hoisted_3 = { class: "font-medium" }
const _hoisted_4 = {
  key: 0,
  class: "font-bold lowercase text-primary-main dark:text-primary-main"
}

import type { PropertyCondition, PropertyConditionOperator, ConditionEditorProperty } from "@/lib/helpers/condition";
import { computed, watchEffect, ref, nextTick, watch } from "vue";
import { useI18n } from "vue-i18n";
import ICON_EDIT from "@/lib/icons/edit.json";
import ICON_TRASH from "@/lib/icons/trash.json";
import ICON_DRAG_HANDLE from "@/lib/icons/burger.json";


export default /*@__PURE__*/_defineComponent({
  __name: 'property',
  props: {
    modelValue: {},
    properties: {},
    showHandle: { type: Boolean }
  },
  emits: ["update:modelValue", "delete"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const { t } = useI18n();
const modal = ref();

const emit = __emit;

const props = __props;

__expose({ openModal });

const propertyOptions = computed(() => {
  if (!props.properties) return null;
  return props.properties.map((x) => ({
    label: x.label,
    value: x.prop
  }));
});

const currentProperty = computed(() => {
  if (props.properties) {
    return props.properties.find((x) => x.prop === props.modelValue.prop);
  } else {
    return null;
  }
});

const propertyValueFromEnum = computed(() => {
  if (currentProperty.value?.type !== "enum") return "";
  if (!currentProperty.value?.values) return "";
  const currentValue = currentProperty.value?.values.find((x) => x.value === props.modelValue.value);
  if (!currentValue) return "";
  return currentValue.label;
});

const currentPropertyValue = computed(() =>
  currentProperty.value?.type === "enum" ? propertyValueFromEnum.value : props.modelValue.value
);

const operators = computed<PropertyConditionOperator[]>(() => {
  switch (currentProperty.value?.type) {
    case "enum":
      return ["eq", "neq"];
    case "string":
      return ["eq", "neq", "sw", "ew", "ct", "nct"];
    case "number":
      return ["eq", "neq", "lt", "le", "gt", "ge"];
    case "yesNo":
      return ["yes", "no"];
    default:
      return [];
  }
});

const operatorOptions = computed(() => operators.value.map((x) => ({ value: x, label: t(`op-${x}`) })));

async function openModal() {
  await nextTick();
  modal.value?.openModal();
}

function onUpdate(newValues: Partial<PropertyCondition>) {
  if (currentProperty.value?.type === "number" && "value" in newValues && typeof newValues.value === "string") {
    var parsedValue = parseFloat(newValues.value);
    if (!isNaN(parsedValue)) {
      newValues.value = parsedValue;
    }
  }
  // Automatically open modal when property is added
  if (props.modelValue.value) {
    openModal();
  }

  emit("update:modelValue", { ...props.modelValue, ...newValues });
}

// Sélectionner la première option des dropdown par défaut
watchEffect(() => {
  if (propertyOptions.value) {
    selectFirstOption("prop", propertyOptions.value);
  }

  if (operatorOptions.value) {
    selectFirstOption("op", operatorOptions.value);
  }

  if (currentProperty.value?.type === "enum") {
    selectFirstOption("value", currentProperty.value.values);
  }
});

function selectFirstOption(property: keyof PropertyCondition, options: { value: string }[]) {
  if (options.length === 0) return;

  const currentOption = options.find((x) => x.value === props.modelValue[property]);
  if (currentOption === undefined) {
    onUpdate({ [property]: options[0].value });
  }
}

// Lorsque le type de propriété change, supprimer la valeur
watch(
  () => currentProperty.value?.type,
  () => onUpdate({ value: undefined })
);

function getMenuItems() {
  return [
    {
      label: t("edit-condition"),
      icon: ICON_EDIT,
      onClick() {
        openModal();
      }
    },
    {
      label: t("delete-condition"),
      icon: ICON_TRASH,
      onClick() {
        emit("delete");
      }
    }
  ];
}

return (_ctx: any,_cache: any) => {
  const _component_PfIcon = _resolveComponent("PfIcon")!
  const _component_PfButtonGroup = _resolveComponent("PfButtonGroup")!
  const _component_PfFormInputSelect = _resolveComponent("PfFormInputSelect")!
  const _component_PfFormInputText = _resolveComponent("PfFormInputText")!
  const _component_PfFormSection = _resolveComponent("PfFormSection")!
  const _component_PfFormLayout = _resolveComponent("PfFormLayout")!
  const _component_UiDialogModal = _resolveComponent("UiDialogModal")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      (__props.showHandle)
        ? (_openBlock(), _createBlock(_component_PfIcon, {
            key: 0,
            class: "handle cursor-move opacity-60",
            size: "sm",
            icon: _unref(ICON_DRAG_HANDLE)
          }, null, 8, ["icon"]))
        : _createCommentVNode("", true),
      _createElementVNode("p", _hoisted_2, [
        _createElementVNode("span", _hoisted_3, _toDisplayString(currentProperty.value?.label), 1),
        (props.modelValue.op)
          ? (_openBlock(), _createElementBlock("span", _hoisted_4, _toDisplayString(_unref(t)(`op-${props.modelValue.op}`)), 1))
          : _createCommentVNode("", true),
        _createElementVNode("span", null, _toDisplayString(currentPropertyValue.value), 1)
      ]),
      _createVNode(_component_PfButtonGroup, {
        class: "ml-auto mr-0",
        "button-size": "sm",
        "tooltip-position": "left",
        items: getMenuItems()
      }, null, 8, ["items"]),
      _cache[4] || (_cache[4] = _createElementVNode("span", { class: "absolute -left-4 top-1/2 h-full w-px -translate-y-full bg-grey-700" }, null, -1)),
      _cache[5] || (_cache[5] = _createElementVNode("span", { class: "absolute -left-4 top-1/2 h-px w-4 bg-grey-700" }, null, -1))
    ]),
    _createVNode(_component_UiDialogModal, {
      ref_key: "modal",
      ref: modal,
      title: _unref(t)('edit-condition')
    }, {
      description: _withCtx(() => [
        _createVNode(_component_PfFormLayout, { class: "text-left" }, {
          default: _withCtx(() => [
            _createVNode(_component_PfFormSection, null, {
              default: _withCtx(() => [
                (propertyOptions.value)
                  ? (_openBlock(), _createBlock(_component_PfFormInputSelect, {
                      key: 0,
                      class: "shrink-0",
                      label: _unref(t)('subject'),
                      options: propertyOptions.value,
                      "model-value": props.modelValue.prop,
                      placeholder: " ",
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = (prop) => onUpdate({ prop }))
                    }, null, 8, ["label", "options", "model-value"]))
                  : _createCommentVNode("", true),
                (currentProperty.value)
                  ? (_openBlock(), _createBlock(_component_PfFormInputSelect, {
                      key: 1,
                      class: "shrink-0",
                      label: _unref(t)('rule'),
                      options: operatorOptions.value,
                      "model-value": props.modelValue.op,
                      "onUpdate:modelValue": _cache[1] || (_cache[1] = (op) => onUpdate({ op }))
                    }, null, 8, ["label", "options", "model-value"]))
                  : _createCommentVNode("", true),
                (currentProperty.value?.type === 'enum')
                  ? (_openBlock(), _createBlock(_component_PfFormInputSelect, {
                      key: 2,
                      class: "grow",
                      label: _unref(t)('select'),
                      options: currentProperty.value.values,
                      "model-value": props.modelValue.value,
                      "onUpdate:modelValue": _cache[2] || (_cache[2] = (value) => onUpdate({ value }))
                    }, null, 8, ["label", "options", "model-value"]))
                  : _createCommentVNode("", true),
                (currentProperty.value && ['string', 'number'].includes(currentProperty.value.type))
                  ? (_openBlock(), _createBlock(_component_PfFormInputText, {
                      key: 3,
                      class: "grow",
                      "input-type": currentProperty.value.type === 'number' ? 'number' : 'text',
                      label: currentProperty.value.type === 'number' ? _unref(t)('quantity') : _unref(t)('value'),
                      value: props.modelValue.value,
                      onInput: _cache[3] || (_cache[3] = (ev) => onUpdate({ value: (ev.target as HTMLInputElement).value }))
                    }, null, 8, ["input-type", "label", "value"]))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["title"])
  ], 64))
}
}

})